<template>
  <div class="axdd">
    <!-- 좌: 서식 정보(접이) + 섹션 목록 -->
    <div class="axdd__panel axdd__panel--left">
      <el-collapse v-model="metaCollapse" class="axdd__meta-collapse">
        <el-collapse-item name="meta">
          <template #title>
            <span class="axdd__meta-title">
              서식 정보
              <span class="axdd__meta-summary">
                {{ draft.meta.name || '(이름 없음)' }} · {{ docTypeLabel }}
              </span>
            </span>
          </template>
          <el-form label-width="80px" size="small">
            <el-form-item label="문서 종류" required>
              <el-select
                v-model="draft.meta.docType"
                :disabled="Boolean(draft.draftId.value)"
                placeholder="선택"
                style="width: 100%"
                @change="onDocTypeChange"
              >
                <el-option
                  v-for="t in docTypes"
                  :key="t.docType"
                  :value="t.docType"
                  :label="t.label"
                />
              </el-select>
              <div v-if="draft.draftId.value" class="axdd__hint">
                저장 후에는 변경할 수 없습니다 (데이터 연결이 전부 무효화됨).
              </div>
            </el-form-item>
            <el-form-item label="서식명" required>
              <el-input :model-value="draft.meta.name" @update:model-value="onNameInput" />
            </el-form-item>
            <el-form-item label="인쇄 제목">
              <el-input
                v-model="draft.layout.page.title"
                @input="draft.titleTouched.value = true"
              />
            </el-form-item>
            <el-form-item label="부제">
              <el-input v-model="draft.layout.page.subtitle" placeholder="(선택)" />
            </el-form-item>
            <el-form-item label="용지 방향">
              <el-radio-group v-model="draft.layout.page.orientation">
                <el-radio-button value="portrait">세로</el-radio-button>
                <el-radio-button value="landscape">가로</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="회사 로고">
              <el-switch v-model="pageShowLogo" />
              <span class="axdd__inline-hint">좌상단 · 시스템 &gt; 회사 관리</span>
            </el-form-item>
            <el-form-item label="발행일시">
              <el-switch v-model="pageShowIssuedAt" />
            </el-form-item>
            <el-form-item label="결재란">
              <el-switch v-model="draft.layout.page.showApprovalLine" />
            </el-form-item>
            <el-form-item v-if="draft.layout.page.showApprovalLine" label="결재 단계">
              <el-radio-group
                :model-value="draft.layout.page.approvalStages === 3 ? 3 : 2"
                @update:model-value="draft.layout.page.approvalStages = $event as 2 | 3"
              >
                <el-radio-button :value="2">2단</el-radio-button>
                <el-radio-button :value="3">3단</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="사본">
              <el-input
                :model-value="(draft.layout.page.copyLabels ?? []).join(', ')"
                placeholder="예: 공급자 보관용, 공급받는자 보관용"
                @update:model-value="onCopyLabelsInput"
              />
              <div class="axdd__hint">입력한 개수만큼 같은 문서를 반복 인쇄합니다 (콤마 구분).</div>
            </el-form-item>
            <el-form-item v-if="(draft.layout.page.copyLabels ?? []).length > 1" label="사본 배치">
              <el-radio-group
                :model-value="draft.layout.page.copySheet ?? 'PER_PAGE'"
                @update:model-value="
                  $event === 'HALF_CUT'
                    ? (draft.layout.page.copySheet = 'HALF_CUT')
                    : delete draft.layout.page.copySheet
                "
              >
                <el-radio-button value="PER_PAGE">한 장씩</el-radio-button>
                <el-radio-button value="HALF_CUT">절취식(한 장 2벌)</el-radio-button>
              </el-radio-group>
              <div class="axdd__hint">
                절취식은 사본 2벌을 한 장에 상·하로 배치하고 절취선을 넣습니다.
              </div>
            </el-form-item>
            <el-form-item label="비고">
              <el-input v-model="draft.meta.note" type="textarea" :rows="2" placeholder="(선택)" />
            </el-form-item>
          </el-form>
        </el-collapse-item>
      </el-collapse>

      <h4 class="axdd__list-head">
        섹션
        <el-dropdown trigger="click" @command="onAddSection">
          <el-button size="small" type="primary" text>+ 추가</el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="HEADER_FIELDS">머리말 필드</el-dropdown-item>
              <el-dropdown-item command="PARTY_BOX">거래 당사자</el-dropdown-item>
              <el-dropdown-item command="TABLE">표</el-dropdown-item>
              <el-dropdown-item command="TOTALS">합계</el-dropdown-item>
              <el-dropdown-item command="CHECKLIST">점검 체크리스트</el-dropdown-item>
              <el-dropdown-item command="TEXT_BLOCK">서술란</el-dropdown-item>
              <el-dropdown-item command="SIGNATURE">서명란</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </h4>
      <AxDocumentSortableList
        :items="sectionRows"
        :selected-key="draft.selectedSectionKey.value"
        empty-text="섹션이 없습니다 — [+ 추가]로 시작하세요."
        @update:items="applySectionOrder"
        @select="selectSection"
        @remove="deleteSection"
      />
    </div>

    <!-- 중앙: 미리보기 = 편집 캔버스 -->
    <div class="axdd__panel axdd__panel--preview">
      <div class="axdd__preview-bar">
        <span class="axdd__preview-label">미리보기 (실제 인쇄 모양)</span>
        <el-select
          v-model="sampleId"
          size="small"
          clearable
          filterable
          class="axdd__sample-select"
          :placeholder="samples.length ? '샘플 데이터 선택' : '샘플 없음 (골격만)'"
          :disabled="samples.length === 0"
          @change="loadSampleData"
        >
          <el-option v-for="s in samples" :key="s.id" :value="s.id" :label="s.label">
            <span>{{ s.label }}</span>
            <span v-if="s.subLabel" class="axdd__sample-sub">{{ s.subLabel }}</span>
          </el-option>
        </el-select>
      </div>
      <el-empty
        v-if="draft.layout.sections.length === 0"
        description="섹션이 없습니다 — 좌측 [+ 추가]로 시작하세요"
        :image-size="80"
      />
      <div v-else class="axdd__canvas" @click="onCanvasClick">
        <AxDocumentRenderer
          :layout="draft.layout"
          :data="previewData"
          mode="view"
          :show-footer="false"
        />
      </div>
    </div>

    <!-- 우: 섹션·필드 속성 -->
    <div class="axdd__panel axdd__panel--right">
      <template v-if="section">
        <div class="axdd__zone">
          <h4>{{ sectionTypeLabel }} 속성</h4>
          <el-form label-width="86px" size="small">
            <el-form-item
              v-if="section.type !== 'SIGNATURE' && section.type !== 'TEXT_BLOCK'"
              label="제목"
            >
              <el-input v-model="draft.selectedSectionTitle.value" placeholder="(선택)" />
            </el-form-item>

            <template v-if="section.type === 'HEADER_FIELDS'">
              <el-form-item label="열 수">
                <el-radio-group v-model="section.columnsPerRow">
                  <el-radio-button :value="2">2</el-radio-button>
                  <el-radio-button :value="3">3</el-radio-button>
                  <el-radio-button :value="4">4</el-radio-button>
                </el-radio-group>
              </el-form-item>
            </template>

            <template v-if="section.type === 'PARTY_BOX'">
              <div v-for="party in section.parties" :key="party.role" class="axdd__party-block">
                <div class="axdd__party-head">
                  {{ party.role === 'SUPPLIER' ? '공급자' : '공급받는자' }}
                </div>
                <el-form-item label="박스 제목">
                  <el-input v-model="party.label" />
                </el-form-item>
                <el-form-item label="정보 출처">
                  <el-radio-group
                    :model-value="party.source"
                    @update:model-value="setPartySource(party, $event as string)"
                  >
                    <el-radio-button value="COMPANY">자사</el-radio-button>
                    <el-radio-button value="CONTEXT">문서 데이터</el-radio-button>
                  </el-radio-group>
                </el-form-item>
                <el-form-item v-if="party.source === 'CONTEXT'" label="데이터 경로">
                  <el-select v-model="party.pathPrefix" filterable allow-create style="width: 100%">
                    <el-option v-for="p in partyPrefixOptions" :key="p" :value="p" :label="p" />
                  </el-select>
                  <div class="axdd__hint">
                    이 경로 아래 name/bizNo/ceo/address/phone 을 읽습니다.
                  </div>
                </el-form-item>
                <el-form-item v-else label="직인">
                  <el-switch v-model="party.showSeal" />
                </el-form-item>
              </div>
            </template>

            <template v-if="section.type === 'TABLE'">
              <el-form-item label="행 방식">
                <el-radio-group
                  :model-value="section.rowMode"
                  @update:model-value="setRowMode(section, $event as string)"
                >
                  <el-radio-button value="BOUND">데이터 연결</el-radio-button>
                  <el-radio-button value="FIXED">고정행</el-radio-button>
                  <el-radio-button value="DYNAMIC">빈 서식</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <template v-if="section.rowMode === 'BOUND'">
                <el-form-item label="연결 목록" required>
                  <el-select v-model="section.dataKey" placeholder="선택" style="width: 100%">
                    <el-option
                      v-for="t in draft.catalog.value?.tables ?? []"
                      :key="t.key"
                      :value="t.key"
                      :label="t.label"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item label="번호 열">
                  <el-switch v-model="section.showRowNo" />
                </el-form-item>
                <el-form-item label="합계행">
                  <el-switch v-model="section.showSummary" />
                  <span class="axdd__inline-hint">컬럼별 「합계」 설정 필요</span>
                </el-form-item>
                <el-form-item label="최소 행">
                  <el-input-number v-model="section.minRows" :min="0" :max="100" />
                  <div class="axdd__hint">데이터가 적어도 이 줄 수까지 빈 칸으로 채웁니다.</div>
                </el-form-item>
              </template>

              <template v-else>
                <el-form-item label="행 머리">
                  <el-input v-model="section.rowHeaderLabel" placeholder="예: 시간" />
                </el-form-item>
                <el-form-item v-if="section.rowMode === 'FIXED'" label="행 라벨">
                  <div class="axdd__inline-list">
                    <AxDocumentSortableList
                      :items="fixedRowRows"
                      variant="inline-edit"
                      empty-text="행이 없습니다."
                      @update:items="applyFixedRowOrder"
                      @update:label="renameFixedRow"
                      @remove="removeFixedRow"
                    />
                    <el-button size="small" text type="primary" @click="addFixedRow"
                      >+ 행 추가</el-button
                    >
                  </div>
                </el-form-item>
                <el-form-item v-else label="행 수">
                  <el-input-number v-model="section.maxRows" :min="1" :max="500" />
                </el-form-item>
              </template>
            </template>

            <template v-if="section.type === 'TOTALS'">
              <el-form-item label="정렬">
                <el-radio-group v-model="section.align">
                  <el-radio-button value="right">우측</el-radio-button>
                  <el-radio-button value="left">좌측</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="합계 항목">
                <div class="axdd__totals-rows">
                  <div v-for="(row, ri) in section.rows" :key="ri" class="axdd__totals-row">
                    <el-input
                      v-model="row.label"
                      size="small"
                      placeholder="라벨"
                      class="axdd__totals-label"
                    />
                    <el-select
                      :model-value="row.bind.path || undefined"
                      size="small"
                      filterable
                      clearable
                      placeholder="데이터 연결"
                      class="axdd__totals-bind"
                      @update:model-value="row.bind.path = ($event as string) ?? ''"
                    >
                      <el-option
                        v-for="opt in scalarBindingOptions"
                        :key="opt.path"
                        :value="opt.path"
                        :label="opt.label"
                      />
                    </el-select>
                    <el-tooltip content="강조 (굵게)" placement="top">
                      <el-switch v-model="row.emphasis" size="small" />
                    </el-tooltip>
                    <el-button
                      text
                      size="small"
                      type="danger"
                      @click="draft.removeTotalRow(section, ri)"
                      >✕</el-button
                    >
                  </div>
                  <el-button size="small" text type="primary" @click="draft.addTotalRow(section)">
                    + 항목 추가
                  </el-button>
                </div>
              </el-form-item>
              <el-form-item label="한글 금액">
                <el-select
                  :model-value="section.amountInWords?.path"
                  filterable
                  clearable
                  placeholder="(표시 안 함)"
                  style="width: 100%"
                  @update:model-value="setAmountInWords(section, $event as string | null)"
                >
                  <el-option
                    v-for="opt in scalarBindingOptions"
                    :key="opt.path"
                    :value="opt.path"
                    :label="opt.label"
                  />
                </el-select>
                <div class="axdd__hint">「일금 ○○○원정」 — 숫자를 연결하면 자동 변환합니다.</div>
              </el-form-item>
            </template>

            <template v-if="section.type === 'CHECKLIST'">
              <el-form-item label="결과 형식">
                <el-radio-group v-model="section.resultType">
                  <el-radio-button value="PASS_FAIL">적합/부적합</el-radio-button>
                  <el-radio-button value="CHECK">체크</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="점검 항목">
                <div class="axdd__inline-list">
                  <AxDocumentSortableList
                    :items="checklistRows"
                    variant="inline-edit"
                    empty-text="점검 항목이 없습니다."
                    @update:items="applyChecklistOrder"
                    @update:label="renameChecklistItem"
                    @remove="removeChecklistItem"
                  />
                  <el-button size="small" text type="primary" @click="addChecklistItem"
                    >+ 항목 추가</el-button
                  >
                </div>
              </el-form-item>
            </template>

            <template v-if="section.type === 'TEXT_BLOCK'">
              <el-form-item label="라벨">
                <el-input v-model="section.label" />
              </el-form-item>
              <el-form-item label="줄 수">
                <el-input-number v-model="section.rows" :min="1" :max="20" />
              </el-form-item>
              <el-form-item label="데이터 연결">
                <el-select
                  :model-value="section.bind?.path"
                  filterable
                  clearable
                  placeholder="(직접 기입용 빈칸)"
                  style="width: 100%"
                  @update:model-value="setTextBlockBind(section, $event as string | null)"
                >
                  <el-option
                    v-for="opt in scalarBindingOptions"
                    :key="opt.path"
                    :value="opt.path"
                    :label="opt.label"
                  />
                </el-select>
              </el-form-item>
            </template>

            <template v-if="section.type === 'SIGNATURE'">
              <el-form-item label="서명자">
                <el-checkbox-group
                  :model-value="[...section.signers]"
                  @update:model-value="
                    section.signers = $event as ('WRITER' | 'REVIEWER' | 'CONFIRMER')[]
                  "
                >
                  <el-checkbox value="WRITER">작성자</el-checkbox>
                  <el-checkbox value="REVIEWER">검토자</el-checkbox>
                  <el-checkbox value="CONFIRMER">확인자</el-checkbox>
                </el-checkbox-group>
              </el-form-item>
            </template>
          </el-form>
        </div>

        <!-- 필드/컬럼 리스트 -->
        <div v-if="section.type === 'HEADER_FIELDS' || section.type === 'TABLE'" class="axdd__zone">
          <h4>
            {{ section.type === 'TABLE' ? '컬럼' : '필드' }}
            <el-button size="small" type="primary" text @click="draft.addField(section)"
              >+ 추가</el-button
            >
          </h4>
          <AxDocumentSortableList
            :items="fieldRows"
            :selected-key="draft.selectedFieldKey.value"
            empty-text="필드가 없습니다 — [+ 추가]"
            @update:items="applyFieldOrder"
            @select="(k) => (draft.selectedFieldKey.value = k)"
            @remove="deleteField"
          />
        </div>

        <!-- 필드 속성 -->
        <div
          v-if="field && (section.type === 'HEADER_FIELDS' || section.type === 'TABLE')"
          class="axdd__zone"
        >
          <h4>필드 속성 — {{ field.label }}</h4>
          <el-form label-width="86px" size="small">
            <el-form-item label="라벨" required>
              <el-input v-model="field.label" />
            </el-form-item>

            <el-form-item label="데이터 연결">
              <el-select
                :model-value="field.bind?.path"
                filterable
                clearable
                placeholder="(직접 기입용 빈칸)"
                style="width: 100%"
                @update:model-value="draft.applyBinding(field, ($event as string | null) ?? null)"
              >
                <el-option
                  v-for="opt in draft.bindingOptions.value"
                  :key="opt.path"
                  :value="opt.path"
                  :label="opt.label"
                >
                  <span>{{ opt.label }}</span>
                  <span class="axdd__opt-path">{{ opt.path }}</span>
                </el-option>
              </el-select>
              <div v-if="isUnknownBinding" class="axdd__warn">
                연결 경로 <code>{{ field.bind?.path }}</code> 는 현재 목록에 없습니다 (값이 비어
                인쇄됨).
              </div>
            </el-form-item>

            <template v-if="field.bind">
              <el-form-item label="표시 형식">
                <el-select
                  :model-value="field.bind.format ?? 'TEXT'"
                  style="width: 100%"
                  @update:model-value="draft.setBindingFormat(field.bind, $event as string)"
                >
                  <el-option
                    v-for="f in FORMAT_OPTIONS"
                    :key="f.value"
                    :value="f.value"
                    :label="f.label"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="빈 값 표기">
                <el-input v-model="field.bind.fallback" placeholder="예: —" />
              </el-form-item>
            </template>
            <template v-else>
              <el-form-item label="입력형">
                <el-select v-model="field.inputType" style="width: 100%">
                  <el-option value="TEXT" label="문자" />
                  <el-option value="NUMBER" label="숫자" />
                  <el-option value="SELECT" label="선택" />
                  <el-option value="PASS_FAIL" label="적합/부적합" />
                  <el-option value="CHECK" label="체크" />
                  <el-option value="DATE" label="날짜" />
                  <el-option value="TIME" label="시각" />
                </el-select>
              </el-form-item>
              <el-form-item v-if="field.inputType === 'SELECT'" label="선택지">
                <el-input
                  :model-value="(field.options ?? []).join(', ')"
                  placeholder="콤마 구분"
                  @update:model-value="field!.options = draft.splitCsv($event)"
                />
              </el-form-item>
            </template>

            <el-form-item label="단위">
              <el-input v-model="field.unit" placeholder="예: ℃, kg" />
            </el-form-item>

            <template v-if="section.type === 'TABLE'">
              <el-form-item label="폭(mm)">
                <el-input-number
                  :model-value="(field as DocColumnDef).width"
                  :min="8"
                  :max="200"
                  :controls="false"
                  placeholder="자동"
                  @update:model-value="setColumnWidth(field, $event)"
                />
                <div class="axdd__hint">
                  비우면 남는 폭을 나눠 갖습니다 (품목명 등 긴 칸에 권장).
                </div>
              </el-form-item>
              <el-form-item label="정렬">
                <el-radio-group
                  :model-value="(field as DocColumnDef).align ?? 'left'"
                  @update:model-value="
                    (field as DocColumnDef).align = $event as 'left' | 'center' | 'right'
                  "
                >
                  <el-radio-button value="left">좌</el-radio-button>
                  <el-radio-button value="center">중</el-radio-button>
                  <el-radio-button value="right">우</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item v-if="section.showSummary" label="합계">
                <el-switch
                  :model-value="(field as DocColumnDef).summary === 'SUM'"
                  @update:model-value="(field as DocColumnDef).summary = $event ? 'SUM' : 'NONE'"
                />
              </el-form-item>
              <el-form-item label="헤더 그룹">
                <el-input v-model="(field as DocColumnDef).group" placeholder="인접 동일명 병합" />
              </el-form-item>
            </template>
          </el-form>
        </div>
        <div
          v-else-if="section.type === 'HEADER_FIELDS' || section.type === 'TABLE'"
          class="axdd__zone-empty"
        >
          필드를 선택하면 속성이 여기 표시됩니다.
        </div>
      </template>
      <el-empty v-else description="왼쪽 목록에서 섹션을 선택하세요" :image-size="60" />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * AxDocumentTemplateDesigner — 문서 서식 디자이너 (좌 목록 / 중 미리보기 / 우 속성).
 *
 * 설계: docs/designs/core-document-template-engine.md §7.6
 * MES HACCP 디자이너(R5)의 3분할 구조를 문서 종류 중립으로 일반화했다.
 * 핵심 차이는 **[데이터 연결] 드롭다운**(공급자 카탈로그)과 **실데이터 미리보기**.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import type {
  DocColumnDef,
  DocFieldDef,
  DocTemplateSection,
  DocumentRenderContext,
  DocumentTypeCatalog,
} from '@axsolutionsdev/shared';

import AxDocumentRenderer from './AxDocumentRenderer.vue';
import AxDocumentSortableList from './designer/AxDocumentSortableList.vue';
import type { SortableRow } from './designer/sortable-row.js';
import {
  SECTION_TYPE_LABELS,
  type DocumentTemplateDraft,
  type PartySection,
  type TotalsSection,
} from './designer/use-document-template-draft.js';
import {
  fetchDocumentCatalog,
  fetchDocumentRender,
  fetchDocumentSamples,
  type DocumentSampleSource,
} from '../../api/documents';
import './document-print.css';

const props = defineProps<{
  draft: DocumentTemplateDraft;
  docTypes: DocumentTypeCatalog[];
}>();

const FORMAT_OPTIONS = [
  { value: 'TEXT', label: '문자' },
  { value: 'DATE', label: '날짜 (YYYY-MM-DD)' },
  { value: 'DATETIME', label: '일시 (YYYY-MM-DD HH:mm)' },
  { value: 'NUMBER', label: '숫자' },
  { value: 'MONEY', label: '금액 (1,234)' },
  { value: 'QTY', label: '수량' },
  { value: 'PERCENT', label: '백분율' },
];

const metaCollapse = ref<string[]>(['meta']);
const samples = ref<DocumentSampleSource[]>([]);
const sampleId = ref<string>('');
const sampleData = ref<DocumentRenderContext | null>(null);

const draft = computed(() => props.draft);
const section = computed(() => draft.value.selectedSection.value);
const field = computed(() => draft.value.selectedField.value);

const docTypeLabel = computed(
  () =>
    props.docTypes.find((t) => t.docType === draft.value.meta.docType)?.label ?? '문서 종류 미선택',
);
const sectionTypeLabel = computed(() =>
  section.value ? (SECTION_TYPE_LABELS[section.value.type] ?? section.value.type) : '',
);

/** 스칼라 바인딩 후보 — TOTALS·TEXT_BLOCK·한글금액처럼 표 밖에서 쓰는 자리. */
const scalarBindingOptions = computed(() =>
  (draft.value.catalog.value?.fields ?? [])
    .filter((f) => f.kind === 'SCALAR')
    .concat(
      draft.value.bindingOptions.value.filter(
        (f) => f.kind === 'SCALAR' && !(draft.value.catalog.value?.fields ?? []).includes(f),
      ),
    ),
);

const partyPrefixOptions = computed(() => {
  const paths = (draft.value.catalog.value?.fields ?? [])
    .filter((f) => f.kind === 'SCALAR' && f.path.includes('.'))
    .map((f) => f.path.slice(0, f.path.lastIndexOf('.')));
  return [...new Set(paths)].filter((p) => p.includes('.'));
});

/** 저장된 bind 경로가 카탈로그에 없으면 경고 — 공급자 필드가 사라졌거나 손으로 넣은 경우. */
const isUnknownBinding = computed(() => {
  const path = field.value?.bind?.path;
  if (!path) return false;
  return !draft.value.bindingOptions.value.some((o) => o.path === path);
});

/** 실데이터 미리보기 — 샘플 미선택이면 골격만 보이는 빈 컨텍스트. */
const previewData = computed<DocumentRenderContext>(
  () =>
    sampleData.value ?? {
      header: {},
      tables: {},
      totals: {},
      company: {
        name: '(자사 상호)',
        bizNo: '',
        ceo: '',
        address: '',
        phone: '',
        bizType: '',
        bizItem: '',
        logoDataUrl: null,
        sealDataUrl: null,
      },
      meta: {
        docType: draft.value.meta.docType,
        sourceId: '',
        issuedAt: '',
        issuedByName: '',
        templateName: draft.value.meta.name,
        templateVersion: draft.value.draftVersion.value,
      },
    },
);

// page 의 optional boolean 은 exactOptionalPropertyTypes 때문에 직접 v-model 을 걸 수 없다.
const pageShowLogo = computed<boolean>({
  get: () => draft.value.layout.page.showCompanyLogo === true,
  set: (v) => {
    if (v) draft.value.layout.page.showCompanyLogo = true;
    else delete draft.value.layout.page.showCompanyLogo;
  },
});
const pageShowIssuedAt = computed<boolean>({
  get: () => draft.value.layout.page.showIssuedAt === true,
  set: (v) => {
    if (v) draft.value.layout.page.showIssuedAt = true;
    else delete draft.value.layout.page.showIssuedAt;
  },
});

// ---------------------------------------------------------------------------
// 목록 행 (좌·우 패널 공용 SortableRow 계약)
// ---------------------------------------------------------------------------

const sectionRows = computed<SortableRow[]>(() =>
  draft.value.layout.sections.map((s) => ({
    key: s.key,
    label: SECTION_TYPE_LABELS[s.type] ?? s.type,
    sublabel: draft.value.sectionDisplayName(s),
  })),
);

const fieldRows = computed<SortableRow[]>(() =>
  draft.value.sectionFields(section.value).map((f) => ({
    key: f.key,
    label: f.label,
    ...(f.bind?.path ? { tags: [{ text: '연결', type: 'primary' as const }] } : {}),
  })),
);

const fixedRowRows = computed<SortableRow[]>(() => {
  const s = section.value;
  if (s?.type !== 'TABLE' || s.rowMode !== 'FIXED') return [];
  return (s.fixedRows ?? []).map((r, i) => ({ key: String(i), label: r.label }));
});

const checklistRows = computed<SortableRow[]>(() => {
  const s = section.value;
  if (s?.type !== 'CHECKLIST') return [];
  return s.items.map((i) => ({ key: i.key, label: i.label }));
});

// ---------------------------------------------------------------------------
// 조작
// ---------------------------------------------------------------------------

function onNameInput(v: string): void {
  draft.value.meta.name = v;
  // 인쇄 제목을 직접 만진 적이 없으면 서식명을 따라간다
  if (!draft.value.titleTouched.value) draft.value.layout.page.title = v;
}

function onCopyLabelsInput(raw: string): void {
  const labels = draft.value.splitCsv(raw);
  if (labels.length) draft.value.layout.page.copyLabels = labels;
  else delete draft.value.layout.page.copyLabels;
}

function onAddSection(type: string): void {
  draft.value.addSection(type, draft.value.selectedSectionKey.value);
}

function selectSection(key: string): void {
  draft.value.selectedSectionKey.value = key;
  draft.value.selectedFieldKey.value = null;
}

function deleteSection(key: string): void {
  draft.value.removeSection(key);
}

function deleteField(key: string): void {
  if (section.value) draft.value.removeField(section.value, key);
}

function applySectionOrder(rows: SortableRow[]): void {
  const byKey = new Map(draft.value.layout.sections.map((s) => [s.key, s]));
  draft.value.layout.sections = rows
    .map((r) => byKey.get(r.key))
    .filter((s): s is DocTemplateSection => Boolean(s));
}

function applyFieldOrder(rows: SortableRow[]): void {
  const s = section.value;
  if (!s) return;
  const fields = draft.value.sectionFields(s);
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const next = rows.map((r) => byKey.get(r.key)).filter((f): f is DocFieldDef => Boolean(f));
  if (s.type === 'HEADER_FIELDS') s.fields = next;
  // DocColumnDef 의 추가 속성은 전부 optional 이라 DocFieldDef[] 가 그대로 대입된다
  else if (s.type === 'TABLE') s.columns = next;
}

function applyFixedRowOrder(rows: SortableRow[]): void {
  const s = section.value;
  if (s?.type !== 'TABLE') return;
  draft.value.setFixedRows(
    s,
    rows.map((r) => r.label),
  );
}

function renameFixedRow(key: string, label: string): void {
  const s = section.value;
  if (s?.type !== 'TABLE' || !s.fixedRows) return;
  const row = s.fixedRows[Number(key)];
  if (row) row.label = label;
}

function removeFixedRow(key: string): void {
  const s = section.value;
  if (s?.type !== 'TABLE' || !s.fixedRows) return;
  s.fixedRows.splice(Number(key), 1);
}

function addFixedRow(): void {
  const s = section.value;
  if (s?.type !== 'TABLE') return;
  s.fixedRows = [...(s.fixedRows ?? []), { label: '' }];
}

function applyChecklistOrder(rows: SortableRow[]): void {
  const s = section.value;
  if (s?.type !== 'CHECKLIST') return;
  const byKey = new Map(s.items.map((i) => [i.key, i]));
  s.items = rows
    .map((r) => byKey.get(r.key))
    .filter((i): i is { key: string; label: string } => Boolean(i));
}

function renameChecklistItem(key: string, label: string): void {
  const s = section.value;
  if (s?.type !== 'CHECKLIST') return;
  const item = s.items.find((i) => i.key === key);
  if (item) item.label = label;
}

function removeChecklistItem(key: string): void {
  const s = section.value;
  if (s?.type !== 'CHECKLIST') return;
  s.items = s.items.filter((i) => i.key !== key);
}

function addChecklistItem(): void {
  const s = section.value;
  if (s?.type !== 'CHECKLIST') return;
  s.items.push({ key: draft.value.newKey('i'), label: `점검 항목 ${s.items.length + 1}` });
}

/** 행 방식 전환 — 모드에 맞지 않는 필드를 정리해 검증 통과 상태를 유지한다. */
function setRowMode(s: DocTemplateSection, mode: string): void {
  if (s.type !== 'TABLE') return;
  if (mode === 'BOUND') {
    s.rowMode = 'BOUND';
    delete s.fixedRows;
    delete s.maxRows;
    s.dataKey = s.dataKey ?? draft.value.catalog.value?.tables?.[0]?.key ?? '';
    if (s.minRows === undefined) s.minRows = 5;
  } else if (mode === 'FIXED') {
    s.rowMode = 'FIXED';
    delete s.dataKey;
    delete s.minRows;
    if (!s.fixedRows?.length) s.fixedRows = [{ label: '' }];
  } else {
    s.rowMode = 'DYNAMIC';
    delete s.dataKey;
    delete s.fixedRows;
    delete s.minRows;
    s.maxRows = s.maxRows ?? 10;
  }
}

function setPartySource(party: PartySection['parties'][number], source: string): void {
  if (source === 'COMPANY') {
    party.source = 'COMPANY';
    delete party.pathPrefix;
  } else {
    party.source = 'CONTEXT';
    delete party.showSeal;
    party.pathPrefix = party.pathPrefix ?? partyPrefixOptions.value[0] ?? 'header.partner';
  }
}

function setAmountInWords(s: TotalsSection, path: string | null): void {
  if (path) s.amountInWords = { path };
  else delete s.amountInWords;
}

function setTextBlockBind(s: DocTemplateSection, path: string | null): void {
  if (s.type !== 'TEXT_BLOCK') return;
  if (path) s.bind = { path };
  else delete s.bind;
}

function setColumnWidth(f: DocFieldDef, v: number | undefined): void {
  const col = f as DocColumnDef;
  if (v === undefined || v === null || Number.isNaN(v)) delete col.width;
  else col.width = v;
}

/** 캔버스에서 섹션 영역을 클릭하면 그 섹션을 선택한다(칸 단위 선택은 P3). */
function onCanvasClick(e: MouseEvent): void {
  const el = (e.target as HTMLElement | null)?.closest?.('.axdoc__section');
  if (!el) return;
  const idx = [...(el.parentElement?.querySelectorAll('.axdoc__section') ?? [])].indexOf(el);
  const key = draft.value.layout.sections[idx]?.key;
  if (key) selectSection(key);
}

// ---------------------------------------------------------------------------
// 카탈로그·샘플 로드
// ---------------------------------------------------------------------------

async function loadCatalog(docType: string): Promise<void> {
  if (!docType) {
    draft.value.catalog.value = null;
    samples.value = [];
    return;
  }
  try {
    const [catalog, sampleRows] = await Promise.all([
      fetchDocumentCatalog(docType),
      fetchDocumentSamples(docType).catch(() => [] as DocumentSampleSource[]),
    ]);
    draft.value.catalog.value = catalog;
    samples.value = sampleRows;
  } catch (e) {
    ElMessage.error(e instanceof Error ? e.message : '문서 종류 정보를 불러오지 못했습니다.');
  }
}

/**
 * 실데이터 미리보기 — 저장 전 draft layout 은 서버로 보낼 수 없으므로,
 * render 응답에서 **데이터만** 취하고 layout 은 draft 를 쓴다 (설계 §7.6.3).
 */
async function loadSampleData(id: string | null): Promise<void> {
  if (!id) {
    sampleData.value = null;
    return;
  }
  try {
    const res = await fetchDocumentRender(draft.value.meta.docType, id);
    sampleData.value = res.context;
  } catch (e) {
    sampleData.value = null;
    ElMessage.error(e instanceof Error ? e.message : '샘플 데이터를 불러오지 못했습니다.');
  }
}

function onDocTypeChange(docType: string): void {
  sampleId.value = '';
  sampleData.value = null;
  void loadCatalog(docType);
}

watch(
  () => draft.value.meta.docType,
  (v, prev) => {
    if (v && v !== prev && draft.value.catalog.value?.docType !== v) void loadCatalog(v);
  },
);

onMounted(() => {
  if (draft.value.meta.docType) void loadCatalog(draft.value.meta.docType);
});

defineExpose({ reloadCatalog: () => loadCatalog(draft.value.meta.docType) });
</script>

<style scoped>
.axdd {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr) 340px;
  gap: 12px;
  height: 100%;
  min-height: 0;
}

.axdd__panel {
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  padding: 10px;
  background: var(--el-bg-color);
}

.axdd__panel--preview {
  background: #e5e7eb;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.axdd__preview-bar {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: var(--el-bg-color);
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.axdd__preview-label {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.axdd__sample-select {
  margin-left: auto;
  width: 240px;
}

.axdd__sample-sub {
  float: right;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  margin-left: 12px;
}

.axdd__canvas {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px 0 24px;
}

/* 미리보기 축소 — A4 실폭(210mm)이 패널보다 넓다 */
.axdd__canvas :deep(.report-page) {
  transform: scale(0.86);
  transform-origin: top center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}

.axdd__canvas :deep(.axdoc__section) {
  cursor: pointer;
  outline: 1px dashed transparent;
  outline-offset: 2px;
}

.axdd__canvas :deep(.axdoc__section:hover) {
  outline-color: var(--el-color-primary-light-5);
}

.axdd__meta-collapse {
  border: none;
}

.axdd__meta-title {
  font-size: 13px;
  font-weight: 600;
}

.axdd__meta-summary {
  font-weight: 400;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  margin-left: 6px;
}

.axdd__list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 10px 0 6px;
  font-size: 13px;
}

.axdd__zone {
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.axdd__zone:last-child {
  border-bottom: none;
}

.axdd__zone h4 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 8px;
  font-size: 13px;
}

.axdd__zone-empty,
.axdd__hint {
  font-size: 11.5px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.axdd__inline-hint {
  font-size: 11.5px;
  color: var(--el-text-color-secondary);
  margin-left: 8px;
}

.axdd__warn {
  font-size: 11.5px;
  color: var(--el-color-warning);
  line-height: 1.5;
}

.axdd__opt-path {
  float: right;
  color: var(--el-text-color-placeholder);
  font-size: 11px;
  margin-left: 16px;
}

.axdd__party-block {
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
}

.axdd__party-head {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
}

.axdd__inline-list {
  width: 100%;
}

.axdd__totals-rows {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.axdd__totals-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.axdd__totals-label {
  width: 90px;
  flex: 0 0 auto;
}

.axdd__totals-bind {
  flex: 1;
  min-width: 0;
}
</style>
